import React from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";

import spinner from "@dhl-official/icons/spinner.svg";
import search from "@dhl-official/icons/search.svg";
import { CITY_AND_POSTAL_CODE } from "./constants";
import {
  useCityAndPostalCode,
  useData,
  useFrequentPostalLocations,
  useSearchPostalLocations,
  useShouldHideInput,
  useValidation,
} from "./hooks";
import { Dropdown, DropdownWrapper } from "./styled";

/**
 * Renders a dropdown for selecting a city and postal code.
 * Hidden inputs are used to store and validate selected city and postal code separately as backend expects them as separate fields.
 */
export const CityAndPostalCode = ({
  cityValue,
  postalCodeValue,
  cityName,
  countryCode,
  disabled,
  hasPostalCode,
  isCityRequired,
  isPostalCodeRequired,
  onLocationSelect,
  postalCodeName,
  section,
  reimaginePostcodesEnabled,
  isBusiness,
}) => {
  const intl = useIntl();
  const shouldHideInput = useShouldHideInput();

  const {
    city,
    onChange,
    onFilter,
    postalCode,
    query,
    value,
  } = useCityAndPostalCode({
    cityName,
    countryCode,
    hasPostalCode,
    postalCodeName,
    onLocationSelect,
    cityValue,
    postalCodeValue,
  });

  const {
    data: frequentPostalLocations,
    loading: frequentPostalLocationsLoading,
  } = useFrequentPostalLocations({ countryCode });

  const {
    data: postalLocations,
    loading: postalLocationsLoading,
  } = useSearchPostalLocations({ countryCode, query });

  const data = useData({
    frequentPostalLocations,
    postalLocations,
    query,
    value,
  });

  const validation = useValidation({
    section,
    isCityRequired,
    isPostalCodeRequired,
  });

  const shouldShowPostalCodeField = reimaginePostcodesEnabled && isBusiness;

  return (
    <React.Fragment>
      {shouldHideInput ? undefined : (
        <DropdownWrapper hasShowPostalcodeField={shouldShowPostalCodeField}>
          <Dropdown
            data={data}
            data-testid={`${section}-${CITY_AND_POSTAL_CODE}`}
            filterIcon={postalLocationsLoading ? spinner : search}
            filterIconAnimation={postalLocationsLoading ? "spin" : "none"}
            filterIconDuration={postalLocationsLoading ? "infinite" : "none"}
            filterPlaceholder={intl.formatMessage({
              id: "tradeLaneComponent.cityAndPostalCodeFilterPlaceholder",
            })}
            isDisabled={disabled || frequentPostalLocationsLoading}
            label={intl.formatMessage({
              id: "tradeLaneComponent.cityAndPostalCodeLabel",
            })}
            name={`${section}-${CITY_AND_POSTAL_CODE}`}
            onDhlChange={onChange}
            onDhlFilter={onFilter}
            placeholder={intl.formatMessage({
              id: "tradeLaneComponent.cityAndPostalCodePlaceholder",
            })}
            required={isCityRequired || isPostalCodeRequired}
            showFilter
            value={value}
            validation={validation}
          />
        </DropdownWrapper>
      )}
      <input
        name={cityName}
        required={isCityRequired}
        type="hidden"
        value={city}
      />
      {hasPostalCode ? (
        <input
          name={postalCodeName}
          required={isPostalCodeRequired}
          type="hidden"
          value={postalCode}
        />
      ) : undefined}
    </React.Fragment>
  );
};

CityAndPostalCode.propTypes = {
  /**
   * Indicates whether or not the reimagined postcodes feature is enabled.
   */
  reimaginePostcodesEnabled: PropTypes.bool,
  /**
   * Selected segment: Business or Private.
   * Controls the visibility of City & Zip Code field.
   */
  isBusiness: PropTypes.bool,
  /**
   * Country code for the locations.
   */
  countryCode: PropTypes.string,
  /**
   * Whether the dropdown is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Name of the city input as identified in the form.
   */
  cityName: PropTypes.string,
  /**
   * Whether the city is required.
   */
  isCityRequired: PropTypes.bool,
  /**
   * Name of the postal code input as identified in the form.
   */
  postalCodeName: PropTypes.string,
  /**
   * Whether the postal code is required.
   */
  isPostalCodeRequired: PropTypes.bool,
  /**
   * Whether the country has postal code or not.
   */
  hasPostalCode: PropTypes.bool,
  /**
   * Which section the dropdown belongs to.
   */
  section: PropTypes.oneOf(["origin", "destination"]),
  /**
   * Callback function when user selects a location from the dropdown.
   */
  onLocationSelect: PropTypes.func,
  /**
   * The selected city value.
   */
  cityValue: PropTypes.string,
  /**
   * The selected postal code value.
   */
  postalCodeValue: PropTypes.string,
};
